import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Mail, Lock, Chrome, AlertCircle, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input, Label } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { ROLE_LABELS, peekInvite } from '../lib/teams';

export function Login() {
  const navigate = useNavigate();
  const { signInWithEmail, signUpWithEmail, signInWithGoogle, isDemo, error } = useAuth();
  // Set by the /join page when someone opens an invite link
  const [invite] = useState(peekInvite);
  // People arriving on an invite link are new, so start them on sign-up
  const [isSignUp, setIsSignUp] = useState(!!invite);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError('');

    try {
      if (isSignUp) {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
      navigate('/');
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setLocalError('');

    try {
      await signInWithGoogle();
      navigate('/');
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-surface-950 dark:via-surface-900 dark:to-surface-950 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
            <Bot className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <CardTitle className="text-2xl">Robotics Team Dashboard</CardTitle>
          <CardDescription>
            {isSignUp ? 'Create an account to get started' : 'Sign in to manage your team'}
          </CardDescription>

          {invite && (
            <div className="mt-4 rounded-lg bg-primary-50 p-3 text-left text-sm text-primary-800 dark:bg-primary-900/30 dark:text-primary-200">
              <div className="flex items-start gap-2">
                <UserPlus className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">
                    You're invited as a {ROLE_LABELS[invite.role].toLowerCase()}
                    {invite.teams.length > 0 && ` on ${invite.teams.join(', ')}`}
                  </p>
                  <p className="mt-1 text-xs opacity-80">
                    Create an account, or sign in if you already have one. An admin will confirm you before you get in.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isDemo && (
            <div className="mt-4 rounded-lg bg-yellow-50 p-3 text-left text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Demo Mode</p>
                  <p className="mt-1 text-xs opacity-80">
                    Firebase is not configured. Enter any email/password to continue. Anything you add is saved in this browser only.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {(localError || error) && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
                {localError || error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Loading...' : isSignUp ? 'Create Account' : 'Sign In'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-200 dark:border-surface-700" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-surface-500 dark:bg-surface-900 dark:text-surface-400">
                Or continue with
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <Chrome className="h-4 w-4" />
            Google
          </Button>

          <p className="mt-6 text-center text-sm text-surface-500">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              {isSignUp ? 'Sign in' : 'Sign up'}
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default Login;
